
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Zap, Users, Target, Globe } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { StandardCard } from "@/components/common/StandardCard";

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-black">
      <div className="container-max section-spacing">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-16"
        >
          <PageHeader
            title="About doozy"
            subtitle="Make it easy, digital solutions doozy. We're revolutionizing how businesses create and manage their digital presence with powerful, intuitive tools."
          />

          {/* Mission Section */}
          <section className="grid gap-8 md:grid-cols-2 items-center">
            <div>
              <h2 className="text-heading-2 mb-4 text-black">
                Our Mission
              </h2>
              <p className="text-body-large text-gray-600 leading-relaxed">
                At doozy, we believe digital tools should be powerful yet simple. We're on a mission 
                to democratize access to professional-grade digital solutions, making it easy for 
                businesses of all sizes to thrive online.
              </p>
            </div>
            <StandardCard hover>
              <div className="text-center">
                <Zap className="w-12 h-12 mx-auto mb-4 text-black" />
                <h3 className="text-heading-4 mb-2 text-black">Powered by Innovation</h3>
                <p className="text-gray-600">
                  Cutting-edge AI and modern technology to streamline your workflow
                </p>
              </div>
            </StandardCard>
          </section>

          {/* Features Grid */}
          <section className="space-y-8">
            <div className="text-center">
              <h2 className="text-heading-2 text-black mb-4">
                What Makes doozy Special
              </h2>
              <p className="text-body-large text-gray-600 max-w-2xl mx-auto">
                Our comprehensive suite of tools is designed to handle every aspect of your digital presence
              </p>
            </div>
            
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <StandardCard hover>
                <Target className="w-8 h-8 mb-4 text-black" />
                <h3 className="text-heading-4 mb-2 text-black">AI-Powered Tools</h3>
                <p className="text-gray-600">
                  Generate content, optimize SEO, and create stunning designs with intelligent automation
                </p>
              </StandardCard>
              
              <StandardCard hover>
                <Users className="w-8 h-8 mb-4 text-black" />
                <h3 className="text-heading-4 mb-2 text-black">User-Centric Design</h3>
                <p className="text-gray-600">
                  Intuitive interfaces that make complex tasks simple and accessible to everyone
                </p>
              </StandardCard>
              
              <StandardCard hover>
                <Globe className="w-8 h-8 mb-4 text-black" />
                <h3 className="text-heading-4 mb-2 text-black">Global Reach</h3>
                <p className="text-gray-600">
                  Scalable solutions that work for local businesses and international enterprises
                </p>
              </StandardCard>
            </div>
          </section>

          {/* About bytehype */}
          <section>
            <StandardCard padding="lg">
              <div className="text-center space-y-6">
                <h2 className="text-heading-2 text-black">
                  Built by bytehype
                </h2>
                <p className="text-body-large text-gray-600 max-w-3xl mx-auto leading-relaxed">
                  doozy is proudly developed by bytehype, a digital innovation company based in Australia. 
                  We specialize in creating cutting-edge web solutions that help businesses succeed in the digital age.
                </p>
                <div className="flex flex-wrap justify-center gap-4 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => window.open('https://bytehype.com.au', '_blank')}
                    className="border-gray-200 hover:bg-gray-50"
                  >
                    Visit bytehype.com.au
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => window.open('https://doozy.space', '_blank')}
                    className="border-gray-200 hover:bg-gray-50"
                  >
                    Explore doozy.space
                  </Button>
                </div>
              </div>
            </StandardCard>
          </section>

          {/* CTA Section */}
          <section className="text-center space-y-6">
            <h2 className="text-heading-2 text-black">
              Ready to Get Started?
            </h2>
            <p className="text-body-large text-gray-600 max-w-2xl mx-auto">
              Join thousands of businesses already using doozy to streamline their digital operations 
              and boost their online presence.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                onClick={() => navigate('/tools')}
                className="bg-black text-white hover:bg-gray-800"
              >
                Explore Tools <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/pricing')}
                className="border-gray-200 hover:bg-gray-50"
              >
                View Pricing
              </Button>
            </div>
          </section>
        </motion.div>
      </div>
    </div>
  );
};

export default About;
